/* eslint-disable no-shadow */
import { CommercialOverrides } from 'components/CommercialOverrides/CommercialOverridesFields';
import React from 'react';
import { GooglePlacesMapInfo } from './GooglePlaces';
import { LocationBT, LocationShort } from './Location';

export interface File {
  id: string,
  name: string,
  size: number,
  mime: string,
}

export interface DayWorkHours {
  begin?: string;
  end?: string;
}

export interface HoursOfOperation {
  monday?: DayWorkHours;
  tuesday?: DayWorkHours;
  wednesday?: DayWorkHours;
  thursday?: DayWorkHours;
  friday?: DayWorkHours;
  saturday?: DayWorkHours;
  sunday?: DayWorkHours;
  new_years_day?: DayWorkHours;
  memorial_day?: DayWorkHours;
  independence_day?: DayWorkHours;
  labor_day?: DayWorkHours;
  thanksgiving_day?: DayWorkHours;
  christmas_eve_day?: DayWorkHours;
  christmas_day?: DayWorkHours;
  new_years_eve_day?: DayWorkHours;
}

export interface DealerProfileLocation {
  hash_key?: string;
  range_key?: string;
  gsi1_hash_key?: string;
  gsi1_range_key?: string;
  gsi2_hash_key?: string;
  gsi2_range_key?: string;
  gsi3_hash_key?: string;
  gsi3_range_key?: string;
  update_user?: string;
  update_date?: string;
  open_for_business?: boolean;
  eligible_for_dealer_locator?: boolean;
  google_places?: GooglePlacesMapInfo;
  geo_code_latitude?: string;
  geo_code_longitude?: string;
  google_places_id?: string;
  retail_service_radius?: string;
  requested_geo_code_update?: boolean;
  requested_geo_code_latitude?: string;
  requested_geo_code_longitude?: string;
  requested_geo_code_explanation?: string;
  st_phone?: string;
  toll_free_number?: string;
  after_hours_phone_number?: string;
  store_email?: string;
  site_url?: string;
  mailing_address1?: string;
  mailing_address2?: string;
  mailing_city?: string;
  mailing_state?: string;
  mailing_zip?: string;
  mailing_country?: string;
  sells_michelin?: boolean;
  sells_bfgoodrich?: boolean;
  sells_uniroyal?: boolean;
  hours_of_operation?: HoursOfOperation;
  has_hours_of_operation?: boolean;
  warehouse_flag?: boolean;
  headquarters_flag?: boolean;
  retail_location_flag?: boolean;
  mobile_only?: boolean;
  pos_type: string;
  servicing_dealer_bt?: string;
  aad_group_bt?: string;
  aad_location?: boolean;
  tad_location?: boolean;
  kad_location?: boolean;
  lt_location?: boolean;
  commercial_truck_location?: boolean;
  store_number?: string;
  passenger_services?: PassengerServices;
  services?: PassengerServices;
  customer?: Customer;
  commercial_information?: CommercialInformation;
  contacts_count: number;
  primary_servicing_distributor?: Customer;
  ship_to_number?: string;
  bill_to_number?: string;
  bill_to_location?: Customer; // Facax was DealerProfileLocation
  contact_location_relationship_count?: number;
  has_pos_system: boolean;
  pos_details?: POSDetails;
  on_locator?: boolean;
}

interface CommercialInformation {
  inter_hwy_info: string,
  service_address_latitude: string,
  service_address_longitude: string,
  service_address: null,
  service_state?: string,
  service_city?: string,
  service_zip?: string,
  service_radius: string,
  county: string,
  phone_toll_free: string,
  phone_after_hours: string,
  secondary_phone_after_hours: string,
  website: string,
  overrides_on_tire_locator: boolean,
  open_for_commercial_business: boolean
}
export interface POSDetails {
  pos_provider: string;
  other_name: string;
  additional_comments: string;
  customer_number_with_pos_provider: string;
  pos_provider_technical_contact_name: string;
  pos_provider_technical_contact_email: string;
  pos_provider_technical_contact_phone: string;
}

interface Services {
  english?: boolean;
  french?: boolean;
  spanish?: boolean;
  processes_national_accounts?: boolean;
  michelin_national_account?: boolean;
  autointegrate?: boolean;
  rec_offroad_specialist?: boolean;
  uhp_specialist?: boolean;
  waiting_area_available?: boolean;
  financing_available?: boolean;
  customer_pickup_drop_off?: boolean;
  child_friendly_waiting_rm?: boolean;
  overnight_key_drop?: boolean;
  loaner_vehicles?: boolean;
  all_wheel_alignment?: boolean;
  computer_balancing?: boolean;
  custom_wheel_sales?: boolean;
  farm_tire_service?: boolean;
  mobile_tire_installation_service?: boolean;
  mobile_service_radius?: string;
  rv_tire_sales?: boolean;
  service_and_mount_hp_tires?: boolean;
  service_and_mount_std_tires?: boolean;
  service_run_flat_tires?: boolean;
  tire_inflation_with_nitrogen?: boolean;
  tire_repairs?: boolean;
  winter_tire_studding?: boolean;
  wheel_sales?: boolean;
  air_conditioning?: boolean;
  battery?: boolean;
  belts_and_hoses?: boolean;
  complete_brake_service?: boolean;
  front_end_axle_cv_repair?: boolean;
  oil_filter_lube?: boolean;
  major_mech_repair?: boolean;
  service_electric_cars?: boolean;
  state_inspection?: boolean;
  shocks_and_struts?: boolean;
  service_tpms?: boolean;
  road_hazard_protection?: boolean;
  tire_warranty?: string;
  towing_service?: boolean;
  roadside_service?: boolean;
  roadside_service_24_hrs?: boolean;
  towing_service_24_hrs?: boolean;
}

export interface Customer {
  hash_key?: string;
  customer_addr1?: string;
  customer_addr2?: string;
  customer_city?: string;
  customer_country?: string;
  customer_county?: string;
  customer_dba_name?: string;
  customer_name?: string;
  customer_number?: string;
  customer_state?: string;
  customer_type?: string;
  customer_zip?: string;
  customer_exit_information?: string;
  parent_company_number?: string;
  home_office_number?: string;
  bill_to_customer?: string;
  ship_to_customer?: string;
  extrnl_cust_id?: string;
  update_date?: string;
  is_valid_physical_address?: boolean;
  gsi3_hash_key?: string
}

export interface PassengerServices {
  hash_key?: string;
  range_key?: string;
  update_user?: string;
  update_date?: string;
  bill_to_number?: string;
  ship_to_number?: string;
  ship_to_phone?: string;
  ship_to_fax?: string;
  gsi1_hash_key?: string;
  gsi1_range_key?: string;
  all_brands?: boolean;
  brands?: string[] | null;
  services?: Services;
  location?: DealerProfileLocation;
  ship_to_customer?: Customer;
  bill_to_customer?: Customer;
}

interface DealerLocatorStatusLTValidations {
  brands_sold: boolean;
  eligible_for_dealer_locator: boolean;
  has_gps_location: boolean;
  has_hours_of_operation: boolean;
  is_valid_phone_number: boolean;
  is_valid_physical_address: boolean;
  retail_location_flag: boolean;
}

interface DealerLocatorCommercialMechanicalValidations {
  is_valid_service_address: boolean;
  store_hours_loaded: boolean;
  has_gps_location: boolean;
  has_mechanical_services: boolean;
  has_mechanics: boolean;
  open_commercial_business: boolean;
  minimum_purchase_requirements: boolean;
}

interface DealerLocatorCommercialTireRetreadValidations {
  is_valid_service_address: boolean;
  has_gps_location: boolean;
  open_commercial_business: boolean;
  store_hours_loaded?: boolean;
  phone_number?: boolean;
}

/* ----- LOCATION SUMMARY ----- */
export interface ProviderLocation {
  contacts_count: string;
  ship_to: ProviderLocation_ShipTo;
  overrides?: CommercialOverrides['overrides'];
  primary_servicing_distributor: ProviderLocation_PrimaryServicingDistributor;
  city: string;
  state: string;
  country: string;
  zip: string;
  headquarters_flag: boolean;
  update_date: string;
  id: string;
  mdm?: { site_id: string; }
  associated_location_type: {
    aad: boolean;
    commercial: boolean;
    kad: boolean;
    lt: boolean;
    tad: boolean;
    oliver: boolean;
  }
  dealer_locator_status: {
    lt: {
      status: boolean;
      validations: DealerLocatorStatusLTValidations;
      extra?: { [key: string]: string }
      overridden?: boolean,
    };
    commercial_mechanical: {
      status: boolean;
      validations: DealerLocatorCommercialMechanicalValidations;
      extra?: { [key: string]: string }
      overridden?: boolean,
    };
    commercial_tire_retread: {
      status: boolean;
      validations: DealerLocatorCommercialTireRetreadValidations;
      extra?: { [key: string]: string }
      overridden?: boolean,
    };
  }
  secondary_st_locations: string[];
  primary_st_locations: string[];
}

export type PossibleValidations =
    DealerLocatorStatusLTValidations
    | DealerLocatorCommercialMechanicalValidations
    | DealerLocatorCommercialTireRetreadValidations;

interface ProviderLocation_ShipTo {
  dba_name: string;
  name: string;
  ship_to_number: string;
  store_number: string;
}

interface ProviderLocation_PrimaryServicingDistributor {
  name: string;
  bill_to_number: string;
  dba_name: string;
}

interface WorkHours {
  monday?: DayWorkHours;
  tuesday?: DayWorkHours;
  wednesday?: DayWorkHours;
  thursday?: DayWorkHours;
  friday?: DayWorkHours;
  saturday?: DayWorkHours;
  sunday?: DayWorkHours;
}

export interface ContactTypeRoleLevels {
  location: string;
  level: SPPContactTypeLevels;
}

export interface ContactTypeRole {
  role_area: SPPContactTypeRoleAreas;
  levels: ContactTypeRoleLevels[];
}

export interface ContactType {
  service: SPPContactTypeServices;
  role_areas: ContactTypeRole[];
  id: string;
}

export interface NotificationSubscription {
  event: string;
  type: string;
  method: string[];
  cc_emails?: string[];
  id?: string;
}

export interface MechanicCertification {
  id: string;
  type: string;
  job: string,
  certification?: string,
  state?: string,
  file?: File,
}

export interface MechanicOutsideCertification {
  id: string;
  type: string;
  other_type_name?: string,
  provided_by: string,
  file?: File,
}

export interface Contact {
  id: string;
  contact_level: string;
  first_name: string;
  last_name: string;
  job_title: string;
  email_address: string;
  cell_phone: string | null;
  work_phone: string | null;
  ext: string;
  fax: string | null;
  preferred_language: string;
  preferred_method: string;
  group: boolean;
  visible_externally: boolean;
  work_hours: WorkHours;
  contact_types?: ContactType[];
  notification_subscription?: NotificationSubscription[];
  certifications?: MechanicCertification[];
  outside_certifications?: MechanicOutsideCertification[];
  update_date?: string;
  created_date?: string;
  update_user?: string;
  create_user?: string;
}

export interface ContactListItem {
  id: string;
  contact_level: string;
  first_name: string;
  last_name: string;
  email_address: string;
  cell_phone: string;
  work_phone: string;
  ext: string;
  preferred_method: string;
  contact_types: ContactType[];
  city: string;
  state: string;
  missing_info: boolean;
}

export interface ContactListItemAction {
  id: string;
  label: string;
  action: (id: string, contact: ContactListItem) => void;
  additionalInfo?: any;
  renderElement?: (additionalInfo: any, contact: ContactListItem) => JSX.Element;
}

/// //////////////////////////////////////////////////////////////////
/// //////////////////////////////////////////////////////////////////
/// REMOVE THIS CODE AFTER SERVICES REFACTOR
/// //////////////////////////////////////////////////////////////////
/// //////////////////////////////////////////////////////////////////

export declare type SPPContactTypeLevels = 'primary' | 'secondary' | 'general';
export declare type SPPContactTypeServices = 'retail' | 'commercial';
export declare type SPPContactTypeRoleAreas = 'accounts_payable'
| 'administrator'
| 'casings'
| 'credit_manager'
| 'edi_administrator'
| 'inside_sales'
| 'inventory'
| 'invoicing'
| 'it_director_system_administrator'
| 'marketing_advertising'
| 'mechanic'
| 'michelinb2b_administrator'
| 'outside_sales'
| 'po'
| 'pos_administrator'
| 'president_ceo_dealer_principal'
| 'purchasing_procurement_manager'
| 'purchasing_team_lead'
| 'service_manager'
| 'store_manager'
| 'tad_coordinator_wholesale_manager'
| 'tax_department'
| 'tire_technician'
| 'rotation_technician';

export enum SPPContactTypeLevelsEnum {
  primary = 'primary',
  secondary = 'secondary',
  general = 'general',
}

export enum SPPContactTypeServicesEnum {
  retail = 'retail',
  commercial = 'commercial',
}

export enum SPPContactTypeRoleAreasEnum {
  accounts_payable = 'accounts_payable',
  administrator = 'administrator',
  casings = 'casings',
  credit_manager = 'credit_manager',
  edi_administrator = 'edi_administrator',
  inside_sales = 'inside_sales',
  inventory = 'inventory',
  invoicing = 'invoicing',
  it_director_system_administrator = 'it_director_system_administrator',
  marketing_advertising = 'marketing_advertising',
  mechanic = 'mechanic',
  michelinb2b_administrator = 'michelinb2b_administrator',
  outside_sales = 'outside_sales',
  po = 'po',
  pos_administrator = 'pos_administrator',
  president_ceo_dealer_principal = 'president_ceo_dealer_principal',
  purchasing_procurement_manager = 'purchasing_procurement_manager',
  purchasing_team_lead = 'purchasing_team_lead',
  service_manager = 'service_manager',
  store_manager = 'store_manager',
  tad_coordinator_wholesale_manager = 'tad_coordinator_wholesale_manager',
  tax_department = 'tax_department',
  tire_technician = 'tire_technician',
  rotation_technician = 'rotation_technician',
}

export enum QueryKeys {
  btLocation = 'btLocation',
  stLocation = 'stLocation',
  shortLocation = 'shortLocation',
  locationSiblings = 'locationSiblings',
  commercialLocation = 'commercial-location',
  commercialERSInformation = 'commercial-ers-information',
  getGeoLocation = 'get-geo-location',
  fileUploadedURL = 'file-uploaded-url',
  promosListing = 'admin-promos-listing',
  b2bFileDownload = 'b2b-file-download',
  technicianServiceAreas = 'technician_service_areas'
}

export enum ChangeLogEntities {
  locationInformation = 'location_information',
  services = 'services',
  contacts = 'contacts',
  products = 'products',
  ers = 'ers',
  btLocation = 'bill_to_location',
  btServices = 'bill_to_services',
  btContacts = 'bill_to_contacts',
  btProducts = 'bill_to_products',
  btErs = 'bill_to_ers',
}

export type TranslateFunction = (s: string, r?: any) => string;

export interface GlobalTextInputProps {
  variant?: 'standard',
  InputProps: any,
  style: any,
  onChange?: (event: React.ChangeEvent<any> | null, value?: any) => void
}

export type {
  LocationBT,
  LocationShort,
};
